import { Box, Center, Spinner, Text, VStack } from '@chakra-ui/react'
import type { SpinnerProps } from '@chakra-ui/react'

import type { TranslateFn } from '../types'
import { createTranslator } from '../utils/createTranslator'
import type { StatusStateLayout } from './statusState'

export type LoadingStateProps = {
  /** Visual layout. Default is the web-app centered look. */
  layout?: StatusStateLayout
  /**
   * Loading text. Defaults to "Loading". Pass an empty string to show only
   * the spinner; `ariaLabel` or the default title then labels the status.
   */
  title?: string
  /**
   * Accessible label when `title` is empty. Defaults to the translated
   * default title.
   */
  ariaLabel?: string
  /** Spinner size. Defaults to `xl`. */
  size?: SpinnerProps['size']
  /** Optional translation function for the default title. */
  t?: TranslateFn
}

/**
 * Centered or alert-style loading indicator with an optional title.
 */
export const LoadingState = ({
  layout = 'centered',
  title,
  ariaLabel,
  size = 'xl',
  t,
}: LoadingStateProps) => {
  const tr = createTranslator(t)
  const defaultTitle = tr('loadingState.title', 'Loading')
  const resolvedTitle = title === undefined ? defaultTitle : title
  const statusLabel = resolvedTitle ? undefined : (ariaLabel ?? defaultTitle)

  const spinner = <Spinner size={size} aria-hidden="true" />
  const label = resolvedTitle ? (
    <Text
      mt={layout === 'alert' ? 4 : 0}
      color={layout === 'centered' ? 'fg.muted' : undefined}
    >
      {resolvedTitle}
    </Text>
  ) : null

  if (layout === 'alert') {
    return (
      <Box
        textAlign="center"
        py={8}
        role="status"
        aria-busy="true"
        aria-label={statusLabel}
      >
        {spinner}
        {label}
      </Box>
    )
  }

  return (
    <Center py="2.5rem" role="status" aria-busy="true" aria-label={statusLabel}>
      <VStack gap={4}>
        {spinner}
        {label}
      </VStack>
    </Center>
  )
}
